import { useEffect, useRef, useState, type RefObject } from "react";

const CARD_HEIGHT = 90;
const GAP = 8;
const DEBOUNCE_MS = 100;

function getColumns(gridEl: Element | null): number {
  if (!gridEl) return 1;
  const template = getComputedStyle(gridEl).gridTemplateColumns;
  if (!template || template === "none") return 1;
  return template.split(" ").length;
}

export function useDynamicPageSize(containerRef: RefObject<HTMLDivElement>) {
  const [pageSize, setPageSize] = useState(0);
  const lastPageSizeRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const calculate = () => {
      const height = el.clientHeight;
      if (height === 0) return;
      const rows = Math.max(1, Math.floor((height + GAP) / (CARD_HEIGHT + GAP)));
      const next = rows * getColumns(el.firstElementChild);
      if (next !== lastPageSizeRef.current) {
        lastPageSizeRef.current = next;
        setPageSize(next);
      }
    };

    const scheduleCalculate = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(calculate, DEBOUNCE_MS);
    };

    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = requestAnimationFrame(calculate);
    });
    const observer = new ResizeObserver(scheduleCalculate);
    observer.observe(el);

    return () => {
      cancelAnimationFrame(rafRef.current);
      observer.disconnect();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [containerRef]);

  return pageSize;
}